// use-team.ts — Teams suite, query
//
// Returns the full TeamDetail (including members) for a single team. Always uses
// the contract `TeamDetail` shape — no FE re-shape.

'use client';

import { ROUTE_CONTRACTS } from '@cabal/shared/api-client';
import type { TeamDetail } from '@cabal/shared/domain/session/team/types.js';
import { useApi } from '@/contexts/api';
import { useRouteAuthEnabled } from '@/hooks/routing';
import { useQueryHook } from '@/lib/query/use-query-hook';
import type { QueryOptions, QueryResult } from '@/types/async-protocol';
import { teamsDetailKey } from './team-query-keys';

export function useTeam(teamId: string | null, options?: QueryOptions): QueryResult<TeamDetail> {
  const api = useApi();
  const enabled = (options?.enabled ?? true) && !!teamId;
  const routeEnabled = useRouteAuthEnabled({
    route: ROUTE_CONTRACTS.teams.get,
    enabled,
  });

  return useQueryHook<TeamDetail>({
    queryKey: teamsDetailKey(teamId ?? ''),
    queryFn: async () => {
      if (!teamId) throw new Error('Team ID is required');
      return api.teams.byId(teamId).get();
    },
    options: {
      enabled: routeEnabled && !!teamId,
      refetchInterval: options?.refetchInterval,
      refetchOnFocus: options?.refetchOnFocus,
    },
  });
}
